import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { FloatingLabelInput } from '../components/FloatingLabelInput';
import { PrimaryButton } from '../components/PrimaryButton';
import imgLogoCherry from '../../assets/logo-cherry-color.webp';
import { customerApiErrorMessage, getMe } from '../../lib/customer-api';
import { RequireCustomer } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';

type CustomerInfo = {
  id: string;
  profile_pic: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  location: string;
  created_at: string | null;
};

const composeName = (first: string | null, last: string | null): string => {
  const parts = [first, last].filter((x): x is string => !!x && x.trim() !== '');
  return parts.join(' ').trim();
};

// Builds the read-only "Location" string from the customer's default
// saved address (getMe now returns it as info.default_address; null when
// the customer has no saved address yet → empty string).
const composeLocation = (addr: any): string => {
  if (!addr) return '';
  const parts = [addr.address_line1, addr.address_line2, addr.city, addr.postcode]
    .filter((x): x is string => !!x && String(x).trim() !== '')
    .map((x) => String(x).trim());
  return parts.join(', ');
};

const composePhone = (
  dial: string | null,
  number: string | null,
): string => {
  if (!number) return '';
  return dial ? `${dial} ${number}`.trim() : number;
};

const formatJoined = (iso: string | null): string => {
  if (!iso) return '';
  try {
    return `Joined in ${new Date(iso).getFullYear()}`;
  } catch {
    return '';
  }
};

export default function YourProfilePage() {
  return (
    <RequireCustomer>
      <YourProfilePageInner />
    </RequireCustomer>
  );
}

function YourProfilePageInner() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<CustomerInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMe();
      const c = res.info ?? {};
      const u = c.user ?? {};
      setInfo({
        id: c.id ?? '',
        profile_pic: u.profile_pic ?? null,
        first_name: u.first_name ?? '',
        last_name: u.last_name ?? '',
        email: u.email ?? '',
        phone: composePhone(u.dial_code ?? null, u.mobile_number ?? null),
        location: composeLocation(c.default_address ?? null),
        created_at: c.created_at ?? null,
      });
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not load your profile.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const displayName = useMemo(() => {
    if (!info) return '';
    return composeName(info.first_name, info.last_name) || info.email || '';
  }, [info]);

  // Initial-letter fallback for customers who haven't uploaded a
  // photo yet — pravatar mock removed so we never show a stranger's
  // face when the real photo is missing.
  const avatarInitial = useMemo(() => {
    const seed = info?.first_name || info?.email || '?';
    return seed.slice(0, 1).toUpperCase();
  }, [info]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 relative">
        {/* Back Button Container */}
        <div className="max-w-[1140px] mx-auto px-4 relative">
          <div className="absolute top-6 left-4 z-30">
            <Link
              to="/account-settings"
              className="w-[48px] h-[48px] bg-[#3a3335]/[0.04] rounded-[14px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors"
            >
              <span className="material-icons text-[#3a3335] text-[20px]">
                chevron_left
              </span>
            </Link>
          </div>
        </div>

        {/* Banner Section */}
        <div className="bg-[#f2eee2] h-[233px] relative flex flex-col items-center justify-center overflow-hidden">
          <div className="relative z-10 w-[144px]">
            <img
              src={imgLogoCherry}
              alt="Cherry Picked"
              className="w-full object-contain"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
            Loading your profile…
          </div>
        ) : info ? (
          <>
            {/* Profile Identity Section */}
            <div className="max-w-[1140px] mx-auto px-4 -mt-[57px] relative z-20 flex flex-col items-center mb-12">
              <div className="w-[114px] h-[114px] rounded-[20px] overflow-hidden mb-6 bg-[#f2eee2] shadow-sm flex items-center justify-center">
                {info.profile_pic ? (
                  <img
                    src={info.profile_pic}
                    alt={displayName || 'Profile'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[44px]">
                    {avatarInitial}
                  </span>
                )}
              </div>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] leading-[32px] tracking-[-0.09px] mb-1 text-center">
                {displayName || 'Your profile'}
              </h1>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
                {formatJoined(info.created_at)}
              </p>
            </div>

            {/* Form Section — read-only summary; edits live on /account-info. */}
            <div className="max-w-[557px] mx-auto px-4 mb-24">
              <div className="space-y-6">
                <FloatingLabelInput
                  label="Location"
                  value={info.location || '—'}
                  onChange={() => undefined}
                  containerHeight="h-[64px]"
                  bottomOnly={true}
                  icon="location_on"
                  disabled
                />

                <FloatingLabelInput
                  label="Phone number"
                  value={info.phone || '—'}
                  onChange={() => undefined}
                  containerHeight="h-[64px]"
                  bottomOnly={true}
                  disabled
                />

                <FloatingLabelInput
                  label="Email"
                  type="email"
                  value={info.email || '—'}
                  onChange={() => undefined}
                  containerHeight="h-[64px]"
                  bottomOnly={true}
                  disabled
                />
              </div>

              {/* Action Button — opens the full edit page. */}
              <div className="mt-12 flex justify-center">
                <PrimaryButton
                  className="w-full max-w-[200px] h-[54px] text-[14px]"
                  onClick={() => navigate('/account-info')}
                >
                  Edit profile
                </PrimaryButton>
              </div>
            </div>
          </>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
